import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api';
const ACCESS_KEY = 'nl_dashboard_access_token';
const REFRESH_KEY = 'nl_dashboard_refresh_token';

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  save(payload: { access: string; refresh: string }) {
    localStorage.setItem(ACCESS_KEY, payload.access);
    localStorage.setItem(REFRESH_KEY, payload.refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

type LogoutHandler = () => void;
let logoutHandler: LogoutHandler | null = null;
export function setOnForcedLogout(fn: LogoutHandler | null) {
  logoutHandler = fn;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token && !config.url?.includes('/auth/login') && !config.url?.includes('/auth/magic-link')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const r = tokenStore.getRefresh();
    if (!r) return null;
    try {
      const resp = await axios.post(
        `${BASE_URL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${r}`, Accept: 'application/json' } },
      );
      tokenStore.save({ access: resp.data.access_token, refresh: resp.data.refresh_token });
      return resp.data.access_token as string;
    } catch {
      tokenStore.clear();
      logoutHandler?.();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { __retry?: boolean }) | undefined;

    if (status === 401 && original && !original.__retry && !original.url?.includes('/auth/refresh')) {
      const next = await refresh();
      if (next) {
        original.__retry = true;
        original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${next}` } as any;
        return apiClient.request(original);
      }
    }
    return Promise.reject(error);
  },
);

export function extractError(error: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as any;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
    }
    return data?.message ?? data?.error ?? error.message ?? fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
